import React, {
  Fragment,
  useEffect,
  useState,
  useCallback,
  useRef,
} from "react";
import moment from "moment";
import { MdRefresh } from "react-icons/md";
import {
  ArrowDown,
  ArrowUp,
  Check,
  ChevronDown,
  Download,
  FilterX,
  Info,
  // Temporarily disabled — restore when order status editing returns
  // Pencil,
  Plus,
  Search as SearchIcon,
  WalletCards,
  X,
} from "lucide-react";
import AdminDatePicker from "../../admin/AdminDatePicker";
import { GetProductsToOrder } from "../../../server/ProductToOrder";
// Temporarily disabled — restore when order status editing returns
// import { orderGet, updateOrderStatus } from "../../../server/Order";
import { orderGet } from "../../../server/Order";
import {
  getTransactionsApiErrorMessage,
  getTransactionsByOrdersBatched,
} from "../../../server/Transactions";
import { updateOrder } from "../../../server/ActiveOrder";
import { setPreload } from "../../../server/CartsOpen";
import { useDispatch, useSelector } from "react-redux";
import PopUp from "../../containers/PopUp";
import ListPagination from "../../containers/ListPagination";
import { useListPagination } from "../../../hooks/useListPagination";
import {
  ALL_STATUSES,
  getStatusClass,
  getStatusText,
} from "../../../utils/orderStatus";
import {
  extractPaymentStatus,
  getPaymentStatusCssClass,
  getPaymentStatusText,
} from "../../../utils/paymentStatus";
import {
  extractTransactionType,
  getTransactionTypeCssClass,
  getTransactionTypeText,
} from "../../../utils/transactionType";
import { formatPrice } from "../../../utils/formatPrice";
import {
  getOrderAmountBeforeCoupon,
  getOrderPaymentBreakdown,
} from "../../../utils/orderPaymentBreakdown";
import { formatTransactionDateTime } from "../../../utils/paymentsDateUtils";
import {
  getOrderAddress,
  getDisplayOrderNumber,
} from "../../../utils/sortUserOrders";
import AnchoredPortal from "../../ui/AnchoredPortal";
import LoadingStatus from "../../ui/LoadingStatus";
import {
  exportOrdersBackupPdf,
  MAX_ORDERS_PDF_EXPORT,
} from "../../../utils/exportOrdersBackupPdf";

const sortTransactionsNewestFirst = (transactions) =>
  [...transactions].sort(
    (a, b) =>
      new Date(b.created || b.updated || 0).getTime() -
      new Date(a.created || a.updated || 0).getTime(),
  );

const OrderPaymentDetailsPopup = ({ order, onClose }) => {
  const transactions = Array.isArray(order.transactions)
    ? sortTransactionsNewestFirst(order.transactions)
    : [];

  return (
    <PopUp width="520px" close={onClose}>
      <div className="history-payment-popup">
        <h3>פרטי תשלום – הזמנה {getDisplayOrderNumber(order)}</h3>
        {transactions.length ? (
          <div className="history-payment-tx-list">
            {transactions.map((tx, txIdx) => {
              const status = extractPaymentStatus(tx);
              const typeKey = extractTransactionType(tx);
              const typeText = getTransactionTypeText(typeKey);
              const typeClass = getTransactionTypeCssClass(typeKey);
              const digits = tx.last_four_digits ?? tx.lastFourDigits;
              const sum = tx.sum ?? tx.amount;
              const date = tx.created ?? tx.updated;

              return (
                <div key={tx.id ?? txIdx} className="history-payment-tx-card">
                  {typeText ? (
                    <div className="history-payment-tx-row">
                      <span className="history-payment-tx-label">
                        סוג פעולה
                      </span>
                      <span
                        className={`history-payment-tx-type history-payment-tx-type--${typeClass}`}
                      >
                        {typeText}
                      </span>
                    </div>
                  ) : null}
                  {status ? (
                    <div className="history-payment-tx-row">
                      <span className="history-payment-tx-label">סטטוס</span>
                      <span
                        className={`history-payment-tx-status history-payment-tx-status--${getPaymentStatusCssClass(status)}`}
                      >
                        {getPaymentStatusText(status)}
                      </span>
                    </div>
                  ) : null}
                  {sum != null ? (
                    <div className="history-payment-tx-row">
                      <span className="history-payment-tx-label">סכום</span>
                      <span className="history-payment-tx-value">
                        ₪{formatPrice(sum)}
                      </span>
                    </div>
                  ) : null}
                  {digits ? (
                    <div className="history-payment-tx-row">
                      <span className="history-payment-tx-label">כרטיס</span>
                      <span className="history-payment-tx-value history-payment-tx-value--ltr">
                        ****{digits}
                      </span>
                    </div>
                  ) : null}
                  {date ? (
                    <div className="history-payment-tx-row">
                      <span className="history-payment-tx-label">תאריך</span>
                      <span className="history-payment-tx-value">
                        {formatTransactionDateTime(date)}
                      </span>
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        ) : (
          <p className="history-payment-empty">
            לא נמצאו רשומות תשלום להזמנה זו.
          </p>
        )}
      </div>
    </PopUp>
  );
};

const OrderAmountCell = ({ order }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const panelRef = useRef(null);
  const amountBeforeCoupon = getOrderAmountBeforeCoupon(order);
  const breakdownRows = getOrderPaymentBreakdown(order);

  useEffect(() => {
    if (!open) return undefined;

    const onPointerDown = (event) => {
      const target = event.target;
      if (btnRef.current?.contains(target)) return;
      if (panelRef.current?.contains(target)) return;
      setOpen(false);
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") setOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="order-amount-cell">
      <p className="price">{formatPrice(amountBeforeCoupon)}</p>
      {breakdownRows.length ? (
        <>
          <button
            ref={btnRef}
            type="button"
            className={`history-table-btn history-table-btn--amount-breakdown${
              open ? " is-open" : ""
            }`}
            onClick={() => setOpen((prev) => !prev)}
            aria-label="פירוט סכום הזמנה"
            title="פירוט סכום הזמנה"
            aria-expanded={open}
          >
            <Plus aria-hidden size={15} strokeWidth={2.4} />
          </button>
          <AnchoredPortal
            anchorRef={btnRef}
            open={open}
            placement="bottom-end"
            offset={8}
          >
            <div
              ref={panelRef}
              className="order-amount-breakdown"
              role="dialog"
              aria-label="פירוט סכום הזמנה"
            >
              <table>
                <tbody>
                  {breakdownRows.map((row) => (
                    <tr key={row.key}>
                      <th scope="row">{row.label}</th>
                      <td>₪{formatPrice(row.amount)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AnchoredPortal>
        </>
      ) : null}
    </div>
  );
};

const AllOrders = () => {
  const dispatch = useDispatch();
  const stationId = useSelector((state) => state.user.stationId);

  const [date, setDate] = useState(new Date());
  const [toDate, setToDate] = useState(new Date());
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredItems, setFilteredItems] = useState([]);
  const [search, setSearch] = useState("");
  const [openDatePickerId, setOpenDatePickerId] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const [activeOrder, setActiveOrder] = useState(false);
  const [openInfo, setOpenInfo] = useState(false);
  const [dataElement, setDataElement] = useState({});
  const [paymentDetailsOrderId, setPaymentDetailsOrderId] = useState(null);

  const [filterStatus, setFilterStatus] = useState("");
  const [filterLateOrders, setFilterLateOrders] = useState(false);
  const [filterPaymentStatus, setFilterPaymentStatus] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);

  // Temporarily disabled — restore when order status editing returns
  // const [editingStatusId, setEditingStatusId] = useState(null);
  // const [updatingStatusId, setUpdatingStatusId] = useState(null);
  const [transactionsLoadError, setTransactionsLoadError] = useState(null);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [exportProgress, setExportProgress] = useState(null);

  const applyFilters = useCallback(() => {
    let filtered = [...items];

    if (search.trim()) {
      const lower = search.toLowerCase();
      filtered = filtered.filter((x) => {
        const name = (x.name || x.CustomerName || "").toLowerCase();
        const orderNumber = String(x.orderNumber ?? "").toLowerCase();
        const id = String(x.id ?? "").toLowerCase();
        return (
          name.includes(lower) ||
          orderNumber.includes(lower) ||
          id.includes(lower)
        );
      });
    }

    if (filterStatus) {
      filtered = filtered.filter((x) => {
        const raw = (
          x.tenantStatus ||
          x.tenantBranchStatus ||
          ""
        ).toUpperCase();
        return raw.includes(filterStatus);
      });
    }

    if (filterPaymentStatus)
      filtered = filtered.filter(
        (x) => x.PaymentStatus === filterPaymentStatus,
      );

    if (filterLateOrders)
      filtered = filtered.filter((x) => x.dateTimeStatus === 3);

    setFilteredItems(filtered);
  }, [items, search, filterStatus, filterPaymentStatus, filterLateOrders]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const getItems = useCallback(
    (dateFrom, dateTo) => {
      let fromArr = dateFrom.split("/");
      let toArr = dateTo.split("/");
      const fromIso = fromArr[2] + "-" + fromArr[1] + "-" + fromArr[0];
      const toDateObj = new Date(
        Number(toArr[2]),
        Number(toArr[1]) - 1,
        Number(toArr[0]),
      );
      toDateObj.setDate(toDateObj.getDate() + 1);
      const toYear = toDateObj.getFullYear();
      const toMonth = String(toDateObj.getMonth() + 1).padStart(2, "0");
      const toDay = String(toDateObj.getDate()).padStart(2, "0");
      const toIso = `${toYear}-${toMonth}-${toDay}`;

      let val = {
        from: fromIso,
        to: toIso,
        stationId: stationId,
      };
      dispatch(setPreload(true));
      setLoading(true);
      setTransactionsLoadError(null);
      orderGet(val)
        .then(async (x) => {
          const orders = Array.isArray(x.data.orders) ? x.data.orders : [];

          setItems(orders);
          setLastRefresh(new Date().toLocaleString("he-IL"));

          const orderIds = orders.map((o) => String(o.id));
          if (!orderIds.length) return;

          try {
            const txRes = await getTransactionsByOrdersBatched(orderIds);
            setTransactionsLoadError(null);
            setItems((prev) =>
              prev.map((o) => ({
                ...o,
                transactions: txRes.data[String(o.id)] ?? [],
              })),
            );
          } catch (err) {
            console.error("error fetching transactions:", err);
            setTransactionsLoadError(getTransactionsApiErrorMessage(err));
          }
        })
        .catch((err) => {
          console.log("error fetching orders:", err);
        })
        .finally(() => {
          setLoading(false);
          dispatch(setPreload(false));
        });
    },
    [dispatch, stationId],
  );

  const fetchOrders = useCallback(() => {
    const fromDate = date.toLocaleDateString("ru-RU").split(".").join("/");
    const toDateStr = toDate.toLocaleDateString("ru-RU").split(".").join("/");
    getItems(fromDate, toDateStr);
    setTimeout(() => window.scrollTo(0, 0), 100);
  }, [date, toDate, getItems]);

  const fetchOrdersRef = useRef(fetchOrders);
  fetchOrdersRef.current = fetchOrders;

  useEffect(() => {
    fetchOrdersRef.current();
  }, [stationId]);

  const setSearchFun = (e) => {
    setSearch(e.target.value);
  };

  const clearSearch = () => {
    setSearch("");
  };

  const applyDateFilter = () => {
    fetchOrders();
  };

  const showInfo = (element) => {
    setOpenInfo(true);
    setDataElement(element);
  };

  const showPaymentDetails = (element) => {
    setPaymentDetailsOrderId(element.id);
  };

  // const setActiveOrderFun = (id) => {
  //   console.log("1setActiveOrderFun id:", id);
  //   if (activeOrder !== id) {
  //         console.log("2setActiveOrderFun id:", id);
  //     let findInd = items.findIndex((x) => x.id === id);
  //     if (findInd !== -1 && !items[findInd].orderItems) {
  //           console.log("3setActiveOrderFun id:", id);
  //       GetProductsToOrder(id)
  //         .then((x) => {
  //               console.log("4setActiveOrderFun id:", id);
  //           const itemsEdit = [...items];
  //           itemsEdit[findInd].orderItems = x.data;
  //           setItems(itemsEdit);
  //           setActiveOrder(id);
  //         })
  //         .catch(() => console.log("error"));
  //     } else {
  //       setActiveOrder(id);
  //     }
  //   } else {
  //     setActiveOrder(false);
  //   }
  // };
  const setActiveOrderFun = (id) => {
    if (activeOrder !== id) {
      const findInd = items.findIndex((x) => x.id === id);
      if (findInd !== -1 && !items[findInd].orderItems) {
        console.log(
          "Fetching products for order id:",
          id,
          "stationId:",
          stationId,
        );
        GetProductsToOrder(id, stationId)
          .then((response) => {
            const productsArray = Array.isArray(response.data)
              ? response.data
              : response.data.orderItems || [];

            const itemsEdit = [...items];
            const order = itemsEdit[findInd];

            // ממפה את המוצרים
            order.orderItems = productsArray.map((item) => ({
              name: item.product?.name || "מוצר לא נמצא",
              quantity: item.quantity,
              collected: item.collected,
              totalPrice: item.totalPrice || item.price * item.quantity,
              Price: item.price,
              productId: item.product?.gtin,
              Id: item.productId,
              product: item.product,
              Comment: item.comment || item.Comment || "",
              status: item.status,
              finalTotalPrice: item.finalTotalPrice,
            }));

            setItems(itemsEdit);
            setActiveOrder(id);
          })
          .catch(() => {});
      } else {
        setActiveOrder(id);
      }
    } else {
      setActiveOrder(false);
    }
  };
  // Reorder uses betweenById only. Rows at index 0–1 / last two cannot move yet;
  // enable those buttons when the API supports beforeById / afterById.
  const moveOrder = async (id, direction) => {
    const orderIndex = filteredItems.findIndex((order) => order.id === id);
    if (orderIndex === -1) return;

    const newFilteredList = [...filteredItems];

    if (direction === "up" && orderIndex > 1) {
      const neighborAbove = filteredItems[orderIndex - 1];
      const current = filteredItems[orderIndex];
      const rankBody = {
        kind: "betweenById",
        before: String(filteredItems[orderIndex - 2].id),
        after: String(neighborAbove.id),
      };

      [newFilteredList[orderIndex], newFilteredList[orderIndex - 1]] = [
        newFilteredList[orderIndex - 1],
        newFilteredList[orderIndex],
      ];

      try {
        await updateOrder(String(current.id), rankBody);
        setFilteredItems(newFilteredList);

        const newItems = [...items];
        const origIndex1 = newItems.findIndex(
          (o) => o.id === newFilteredList[orderIndex - 1].id,
        );
        const origIndex2 = newItems.findIndex(
          (o) => o.id === newFilteredList[orderIndex].id,
        );
        if (origIndex1 !== -1 && origIndex2 !== -1) {
          [newItems[origIndex1], newItems[origIndex2]] = [
            newItems[origIndex2],
            newItems[origIndex1],
          ];
          setItems(newItems);
        }
      } catch (error) {
        console.error("Error moving order:", error);
      }
    } else if (direction === "down" && orderIndex < filteredItems.length - 2) {
      const current = filteredItems[orderIndex];
      const neighborBelow = filteredItems[orderIndex + 1];
      const rankBody = {
        kind: "betweenById",
        before: String(neighborBelow.id),
        after: String(filteredItems[orderIndex + 2].id),
      };

      [newFilteredList[orderIndex], newFilteredList[orderIndex + 1]] = [
        newFilteredList[orderIndex + 1],
        newFilteredList[orderIndex],
      ];

      try {
        await updateOrder(String(current.id), rankBody);
        setFilteredItems(newFilteredList);

        const newItems = [...items];
        const origIndex1 = newItems.findIndex(
          (o) => o.id === newFilteredList[orderIndex + 1].id,
        );
        const origIndex2 = newItems.findIndex(
          (o) => o.id === newFilteredList[orderIndex].id,
        );
        if (origIndex1 !== -1 && origIndex2 !== -1) {
          [newItems[origIndex1], newItems[origIndex2]] = [
            newItems[origIndex2],
            newItems[origIndex1],
          ];
          setItems(newItems);
        }
      } catch (error) {
        console.error("Error moving order:", error);
      }
    }
  };

  // Temporarily disabled — restore when order status editing returns
  // const handleStatusChange = async (orderId, newStatus) => {
  //   if (!newStatus) {
  //     setEditingStatusId(null);
  //     return;
  //   }
  //
  //   const orderIndex = items.findIndex((o) => o.id === orderId);
  //   if (orderIndex === -1) {
  //     setEditingStatusId(null);
  //     return;
  //   }
  //
  //   const previousStatus =
  //     items[orderIndex].tenantStatus || items[orderIndex].tenantBranchStatus;
  //   if (newStatus === previousStatus) {
  //     setEditingStatusId(null);
  //     return;
  //   }
  //
  //   setUpdatingStatusId(orderId);
  //
  //   const updatedItems = [...items];
  //   updatedItems[orderIndex] = {
  //     ...updatedItems[orderIndex],
  //     tenantStatus: newStatus,
  //   };
  //   setItems(updatedItems);
  //
  //   try {
  //     await updateOrderStatus(orderId, newStatus, stationId);
  //   } catch (error) {
  //     console.error("Error updating order status:", error);
  //     const revertedItems = [...items];
  //     revertedItems[orderIndex] = {
  //       ...revertedItems[orderIndex],
  //       tenantStatus: previousStatus,
  //     };
  //     setItems(revertedItems);
  //     alert("שגיאה בעדכון סטטוס ליקוט");
  //   } finally {
  //     setUpdatingStatusId(null);
  //     setEditingStatusId(null);
  //   }
  // };

  const clearFilters = () => {
    setSearch("");
    setFilterStatus("");
    setFilterLateOrders(false);
    setFilterPaymentStatus("");
  };

  const handleDownloadPdf = async () => {
    if (exportingPdf) return;
    if (!filteredItems.length) {
      window.alert("אין הזמנות להורדה לפי הסינון הנוכחי.");
      return;
    }
    if (!stationId) {
      window.alert("לא נבחרה תחנה. יש לבחור תחנה לפני ההורדה.");
      return;
    }

    const allOrders = filteredItems;
    const batchSize = MAX_ORDERS_PDF_EXPORT;
    const totalParts = Math.ceil(allOrders.length / batchSize);

    if (allOrders.length > batchSize) {
      const startOk = window.confirm(
        `נמצאו ${allOrders.length} הזמנות לפי הסינון.\n` +
          `ההורדה תתבצע בקבוצות של עד ${batchSize} הזמנות לקובץ PDF (סה״כ ${totalParts} קבצים).\n` +
          `אחרי כל קבוצה תשאל אם להמשיך לקבוצה הבאה.\n\n` +
          `להתחיל בקבוצה 1 מתוך ${totalParts}?`,
      );
      if (!startOk) return;
    }

    setExportingPdf(true);
    dispatch(setPreload(true));

    try {
      for (let part = 0; part < totalParts; part++) {
        const start = part * batchSize;
        const batch = allOrders.slice(start, start + batchSize);

        if (part > 0) {
          const continueOk = window.confirm(
            `קבוצה ${part} מתוך ${totalParts} הורדה בהצלחה.\n` +
              `להמשיך לקבוצה ${part + 1} מתוך ${totalParts} ` +
              `(הזמנות ${start + 1}–${start + batch.length} מתוך ${allOrders.length})?`,
          );
          if (!continueOk) {
            window.alert(
              `ההורדה הופסקה אחרי ${part} קבוצות.\n` +
                `אפשר לחזור ולהמשיך מאוחר יותר עם סינון מתאים.`,
            );
            break;
          }
        }

        setExportProgress({
          current: 0,
          total: batch.length,
          part: part + 1,
          totalParts,
        });

        const result = await exportOrdersBackupPdf({
          orders: batch,
          branch: stationId,
          fromDate: date,
          toDate: toDate,
          search,
          filterStatus,
          filterPaymentStatus,
          filterLateOrders,
          partNumber: part + 1,
          totalParts,
          batchStartIndex: start,
          displayTotal: allOrders.length,
          onProgress: (current, total) =>
            setExportProgress({
              current,
              total,
              part: part + 1,
              totalParts,
            }),
        });

        if (result.failedItemLoads > 0) {
          window.alert(
            `קבוצה ${part + 1}: הורדו ${result.orderCount} הזמנות, אך ל־${result.failedItemLoads} לא נטענו פריטים מהשרת.`,
          );
        }

        if (part === totalParts - 1) {
          window.alert(
            totalParts > 1
              ? `ההורדה הושלמה: ${totalParts} קבצים · ${allOrders.length} הזמנות.`
              : `הקובץ הורד עם ${result.orderCount} הזמנות.`,
          );
        }
      }
    } catch (err) {
      console.error("PDF export failed:", err);
      if (err?.message === "NO_ORDERS") {
        window.alert("אין הזמנות להורדה לפי הסינון הנוכחי.");
      } else if (err?.message === "NO_BRANCH") {
        window.alert("לא נבחרה תחנה. יש לבחור תחנה לפני ההורדה.");
      } else {
        window.alert("שגיאה ביצירת קובץ ה־PDF. נסו שוב.");
      }
    } finally {
      setExportingPdf(false);
      setExportProgress(null);
      dispatch(setPreload(false));
    }
  };

  const uniquePaymentStatuses = [
    ...new Set(items.map((item) => item.PaymentStatus).filter(Boolean)),
  ];

  const paymentDetailsOrder =
    paymentDetailsOrderId != null
      ? items.find((order) => order.id === paymentDetailsOrderId)
      : null;

  const {
    toShow,
    pCounter,
    cFrom,
    cTo,
    isRowVisible,
    showPagination,
    showPaggination,
    UpPaggination,
    downPaggination,
    toShowFun,
  } = useListPagination(filteredItems.length);

  return (
    <div className="all-orders admin-page admin-page--fill">
      <header className="admin-page__header">
        <div className="admin-page__header-start">
          <h1 className="title-role">כל ההזמנות</h1>
          {lastRefresh ? (
            <span className="admin-page__meta">
              שעת ריענון: {lastRefresh} · {filteredItems.length} מתוך{" "}
              {items.length}
            </span>
          ) : null}
        </div>
        <div className="admin-page__header-end">
          <button
            type="button"
            className="admin-labeled-btn admin-labeled-btn--primary"
            onClick={handleDownloadPdf}
            disabled={exportingPdf || loading || !filteredItems.length}
            title="הורד PDF של היסטוריית הזמנות לפי הסינון הנוכחי"
            aria-label="הורד PDF של היסטוריית הזמנות"
          >
            <Download aria-hidden size={18} strokeWidth={2} />
            <span>
              {exportingPdf
                ? exportProgress
                  ? exportProgress.totalParts > 1
                    ? `מכין PDF ${exportProgress.part}/${exportProgress.totalParts}… ${exportProgress.current}/${exportProgress.total}`
                    : `מכין PDF… ${exportProgress.current}/${exportProgress.total}`
                  : "מכין PDF…"
                : "הורד PDF"}
            </span>
          </button>
          <button
            type="button"
            className="admin-page__icon-btn"
            onClick={fetchOrders}
            title="רענן"
            aria-label="רענן הזמנות"
            disabled={exportingPdf}
          >
            <MdRefresh />
          </button>
        </div>
      </header>

      {transactionsLoadError ? (
        <div className="transactions-load-error" role="alert">
          <p>שגיאה בטעינת סטטוסי תשלום: {transactionsLoadError}</p>
        </div>
      ) : null}

      <div className="history-filters admin-filter-bar admin-filter-bar--spread">
        <button
          type="button"
          className="history-filters__toggle"
          onClick={() => setFiltersOpen((p) => !p)}
          aria-expanded={filtersOpen}
        >
          <span>סינון נוסף</span>
          <span className="history-filters__toggle-arrow">
            {filtersOpen ? "▲" : "▼"}
          </span>
        </button>

        <div className="admin-filter-bar__primary history-filters__row">
          <div className="history-filters__date-group">
            <AdminDatePicker
              label="מתאריך"
              value={date}
              onChange={setDate}
              maxDate={toDate}
              openPickerId={openDatePickerId}
              onOpenPickerIdChange={setOpenDatePickerId}
            />

            <AdminDatePicker
              label="עד תאריך"
              value={toDate}
              onChange={setToDate}
              minDate={date}
              openPickerId={openDatePickerId}
              onOpenPickerIdChange={setOpenDatePickerId}
            />

            <button
              type="button"
              className="apply-date-btn admin-labeled-btn admin-labeled-btn--primary"
              onClick={applyDateFilter}
              title="החל תאריכים"
              aria-label="החל תאריכים"
            >
              <Check aria-hidden size={18} strokeWidth={2} />
              <span>החל</span>
            </button>
          </div>

          <div className="history-search admin-search-affix">
            <input
              className="admin-input"
              onChange={setSearchFun}
              value={search}
              type="text"
              placeholder="שם לקוח או מס׳ הזמנה ללקוח..."
              aria-label="חיפוש לפי שם לקוח או מס׳ הזמנה ללקוח"
            />
            {search ? (
              <button
                type="button"
                className="admin-search-affix__clear"
                onClick={clearSearch}
                aria-label="נקה חיפוש"
                title="נקה חיפוש"
              >
                <X aria-hidden size={18} strokeWidth={2} />
              </button>
            ) : (
              <span className="admin-search-affix__icon" aria-hidden>
                <SearchIcon size={18} strokeWidth={2} />
              </span>
            )}
          </div>
        </div>

        <div
          className={`history-filters__panel${filtersOpen ? " is-open" : ""}`}
        >
          <div className="admin-filter-bar__secondary history-filters__row history-filters__row--secondary">
            <div className="history-filter-select admin-filter-grow">
              <select
                className="admin-input admin-filter-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="">כל סטטוסי הליקוט</option>
                {ALL_STATUSES.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
            </div>

            <div className="history-filter-select admin-filter-grow">
              <select
                className="admin-input admin-filter-select"
                value={filterPaymentStatus}
                onChange={(e) => setFilterPaymentStatus(e.target.value)}
              >
                <option value="">כל סטטוסי התשלום</option>
                {uniquePaymentStatuses.map((status, index) => (
                  <option key={index} value={status}>
                    {getPaymentStatusText(status)}
                  </option>
                ))}
              </select>
            </div>

            <label className="history-filter-checkbox">
              <input
                type="checkbox"
                checked={filterLateOrders}
                onChange={(e) => setFilterLateOrders(e.target.checked)}
              />
              <span>הזמנות באיחור בלבד</span>
            </label>

            <button
              type="button"
              className="clear-filters-btn admin-labeled-btn admin-labeled-btn--ghost admin-filter-end"
              onClick={clearFilters}
              title="נקה פילטרים"
              aria-label="נקה פילטרים"
            >
              <FilterX aria-hidden size={18} strokeWidth={2} />
              <span>נקה פילטרים</span>
            </button>
          </div>
        </div>
      </div>

      <div className="admin-page__body history-body">
        {loading && !filteredItems.length ? (
          <LoadingStatus />
        ) : !filteredItems.length ? (
          <h1 className="no-products">לא נמצאו הזמנות התואמות את הפילטרים</h1>
        ) : (
          <>
            <ListPagination
              className="history-pagination"
              showPagination={showPagination}
              toShow={toShow}
              toShowFun={toShowFun}
              cFrom={cFrom}
              cTo={cTo}
              pCounter={pCounter}
              showPaggination={showPaggination}
              downPaggination={downPaggination}
              UpPaggination={UpPaggination}
            />
            <div className="history-table-scroll">
              <table className="history-table">
                <thead>
                  <tr>
                    <th>מיון</th>
                    <th></th>
                    <th>#</th>
                    <th>מס׳ הזמנה ללקוח</th>
                    <th>שם לקוח</th>
                    <th>כתובת משלוח</th>
                    <th>זמן ותאריך</th>
                    {/* <th>מס מוצרים</th> */}
                    <th>סכום הזמנה</th>
                    <th>סכום לוקט</th>
                    <th>פרטי לקוח</th>
                    <th>סטטוס ליקוט</th>
                    <th>תשלומים</th>
                    <th>הערת לקוח</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredItems.map((element, index) => {
                    if (!isRowVisible(index)) return null;

                    // Disabled at list edges until beforeById / afterById are available.
                    const canMoveUp = index > 1;
                    const canMoveDown = index < filteredItems.length - 2;
                    const tenantStatus =
                      element.tenantStatus || element.tenantBranchStatus;

                    return (
                      <Fragment key={element.id}>
                        <tr
                          className={
                            activeOrder === element.id ? "item active" : "item"
                          }
                        >
                          <td data-label="מיון">
                            <div className="history-move-buttons">
                              <button
                                type="button"
                                className="history-table-btn"
                                disabled={!canMoveUp}
                                onClick={() =>
                                  canMoveUp && moveOrder(element.id, "up")
                                }
                                aria-label="הזז למעלה"
                                title="הזז למעלה"
                              >
                                <ArrowUp
                                  aria-hidden
                                  size={16}
                                  strokeWidth={2}
                                />
                              </button>
                              <button
                                type="button"
                                className="history-table-btn"
                                disabled={!canMoveDown}
                                onClick={() =>
                                  canMoveDown && moveOrder(element.id, "down")
                                }
                                aria-label="הזז למטה"
                                title="הזז למטה"
                              >
                                <ArrowDown
                                  aria-hidden
                                  size={16}
                                  strokeWidth={2}
                                />
                              </button>
                            </div>
                          </td>
                          <td className="order-expand-cell">
                            <button
                              type="button"
                              onClick={() => setActiveOrderFun(element.id)}
                              className={`history-table-btn history-table-btn--expand${
                                activeOrder === element.id ? " is-open" : ""
                              }`}
                              aria-label="פתח פרטי הזמנה"
                              title="פתח פרטי הזמנה"
                              aria-expanded={activeOrder === element.id}
                            >
                              <ChevronDown
                                aria-hidden
                                size={18}
                                strokeWidth={2}
                              />
                            </button>
                          </td>
                          <td data-label="#">
                            <p className="history-order-id">{element.id}</p>
                          </td>
                          <td data-label="מס׳ הזמנה ללקוח">
                            <p className="history-order-id">
                              {getDisplayOrderNumber(element)}
                            </p>
                          </td>
                          <td className="order-name-cell">
                            <p className="history-customer-name">
                              {element.name
                                ? element.name
                                : element.CustomerName}
                            </p>
                          </td>
                          <td data-label="כתובת משלוח">
                            <p
                              className="history-order-address"
                              title={getOrderAddress(element)}
                            >
                              {getOrderAddress(element) || "-"}
                            </p>
                          </td>
                          <td data-label="זמן ותאריך">
                            <p className="history-order-date">
                              {moment(element.created).format("DD/MM/YY HH:mm")}
                            </p>
                          </td>
                          {/* <td>
                      <p>{element.orderItems?.length}</p>
                    </td> */}
                          <td data-label="סכום הזמנה">
                            <OrderAmountCell order={element} />
                          </td>
                          <td data-label="סכום לוקט">
                            <p className="price">
                              {Number(element.finalTotalSum ?? 0).toFixed(2)}
                            </p>
                          </td>
                          <td data-label="פרטי לקוח">
                            <button
                              type="button"
                              onClick={() => showInfo(element)}
                              className="history-table-btn"
                              aria-label="פרטי לקוח"
                              title="פרטי לקוח"
                            >
                              <Info aria-hidden size={17} strokeWidth={2} />
                            </button>
                          </td>
                          <td data-label="סטטוס ליקוט">
                            <div className="status-cell">
                              {/* Temporarily disabled — restore when order status editing returns
                              {editingStatusId === element.id ? (
                                <select
                                  autoFocus
                                  disabled={updatingStatusId === element.id}
                                  value={tenantStatus || ""}
                                  onChange={(e) =>
                                    handleStatusChange(
                                      element.id,
                                      e.target.value,
                                    )
                                  }
                                  onBlur={() => setEditingStatusId(null)}
                                >
                                  <option value="">בחר סטטוס</option>
                                  {ALL_STATUSES.map((status) => (
                                    <option
                                      key={status.value}
                                      value={status.value}
                                    >
                                      {status.label}
                                    </option>
                                  ))}
                                </select>
                              ) : (
                                <>
                                  <span
                                    className={`history-status-badge ${getStatusClass(
                                      tenantStatus,
                                    )}`}
                                  >
                                    {getStatusText(tenantStatus)}
                                  </span>
                                  <button
                                    type="button"
                                    className="history-table-btn history-table-btn--edit"
                                    onClick={() =>
                                      setEditingStatusId(element.id)
                                    }
                                    title="עריכת סטטוס ליקוט"
                                    aria-label="עריכת סטטוס ליקוט"
                                  >
                                    <Pencil
                                      aria-hidden
                                      size={15}
                                      strokeWidth={2}
                                    />
                                  </button>
                                </>
                              )}
                              */}
                              <span
                                className={`history-status-badge ${getStatusClass(
                                  tenantStatus,
                                )}`}
                              >
                                {getStatusText(tenantStatus)}
                              </span>
                            </div>
                          </td>
                          <td data-label="תשלומים">
                            <button
                              type="button"
                              className="history-table-btn history-table-btn--payments"
                              onClick={() => showPaymentDetails(element)}
                              aria-label={`תשלומים להזמנה ${getDisplayOrderNumber(element)}`}
                              title="פירוט תשלומים"
                            >
                              <WalletCards
                                aria-hidden
                                size={18}
                                strokeWidth={1.75}
                              />
                            </button>
                          </td>
                          <td data-label="הערת לקוח">
                            <p className="customer-comment">
                              {element.comment || "-"}
                            </p>
                          </td>
                        </tr>
                        {activeOrder === element.id &&
                        element.orderItems &&
                        element.orderItems.length ? (
                          <>
                            <tr className="product-list">
                              <td></td>
                              <td></td>
                              <td></td>
                              <td>
                                <p>שם מוצר</p>
                              </td>
                              <td>
                                <p>ברקוד</p>
                              </td>
                              <td>
                                <p>כמות</p>
                              </td>
                              <td>
                                <p>מחיר</p>
                              </td>
                              <td colSpan="6">
                                <p>הערות</p>
                              </td>
                            </tr>
                            {element.orderItems.map((el) => {
                              return (
                                <tr key={el.Id} className="product">
                                  <td></td>
                                  <td></td>
                                  <td></td>
                                  <td data-label="מוצר">
                                    <p>{el.name}</p>
                                  </td>
                                  <td data-label="ברקוד">
                                    <p>{el.productId}</p>
                                  </td>
                                  <td data-label="כמות">
                                    <p>{el.quantity}</p>
                                  </td>
                                  <td data-label="מחיר">
                                    <p className="price">
                                      {formatPrice(el.totalPrice)}
                                    </p>
                                  </td>
                                  <td colSpan="6" data-label="הערה">
                                    <p>{el.Comment}</p>
                                  </td>
                                </tr>
                              );
                            })}
                            {element.couponDiscount && (
                              <tr className="product coupon-row">
                                <td></td>
                                <td></td>
                                <td></td>
                                <td>הנחת קופון</td>
                                <td></td>
                                <td></td>
                                <td></td>
                                <td className="price">
                                  -{element.couponDiscount.toFixed(2)}
                                </td>
                                <td></td>
                                <td colSpan="4"></td>
                              </tr>
                            )}
                          </>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {openInfo ? (
        <PopUp width="600px" close={() => setOpenInfo(false)}>
          <div className="comment-popup">
            <p>מס׳ הזמנה ללקוח: {getDisplayOrderNumber(dataElement)}</p>
            <p>
              שם:{" "}
              {dataElement.name ? dataElement.name : dataElement.CustomerName}
            </p>
            <p>
              מייל: {dataElement.email ? dataElement.email : dataElement.Email}
            </p>
            <p>
              טלפון: {dataElement.phone ? dataElement.phone : dataElement.Phone}
            </p>
            <p>כתובת משלוח: {getOrderAddress(dataElement) || "-"}</p>
            {dataElement.comment ? <p>הערה: {dataElement.comment}</p> : null}
          </div>
        </PopUp>
      ) : null}

      {paymentDetailsOrder ? (
        <OrderPaymentDetailsPopup
          order={paymentDetailsOrder}
          onClose={() => setPaymentDetailsOrderId(null)}
        />
      ) : null}
    </div>
  );
};

export default AllOrders;
